// Import assets
import coverImage from '../assets/cover.jpg'
import iconFormat1 from '../assets/icon-format-1.svg'
import iconFormat2 from '../assets/icon-format-2.svg'

// Helper function to get asset URL
function getAssetUrl(asset: string) {
  // In production, Vite will replace this with the actual asset URL
  return asset
}

export const mockSubjects = {
  status: 'success',
  total: 2,
  items: [
    {
      slug: 'ela',
      name: 'ELA',
      title: 'English Language Arts'
    },
    {
      slug: 'math',
      name: 'Math',
      title: 'Mathematics'
    }
  ]
};

export const mockFormats = {
  status: 'success',
  total: 3,
  items: [
    {
      slug: 'core-comprehensive',
      name: 'Core: Comprehensive',
      type: 'Core',
      subtype: 'Comprehensive',
      icon: {
        uri: getAssetUrl(iconFormat2),
        name: 'Core Comprehensive',
        extension: '.svg',
        mime_type: 'image/svg+xml',
        alt: 'Core Comprehensive Icon',
        width: 26,
        height: 26
      }
    },
    {
      slug: 'supplemental',
      name: 'Supplemental',
      type: 'Supplemental',
      subtype: 'General',
      icon: {
        uri: getAssetUrl(iconFormat1),
        name: 'Supplemental',
        extension: '.svg',
        mime_type: 'image/svg+xml',
        alt: 'Supplemental Icon',
        width: 26,
        height: 26
      }
    }
  ]
};

export const mockGrades = {
  status: 'success',
  total: 4,
  items: [
    {
      slug: 'k',
      name: 'K',
      title: 'Kindergarten',
      order: 0
    },
    {
      slug: 'first-grade',
      name: '1',
      title: 'First Grade',
      order: 1
    },
    {
      slug: 'second-grade',
      name: '2',
      title: 'Second Grade',
      order: 2
    }
  ]
};

export const mockFilterGroups = {
  status: 'success',
  total: 3,
  items: [
    {
      slug: 'technology-information',
      label: 'Technology Information',
      options: [
        {
          name: 'digital',
          label: 'Digital',
          value: 'digital',
          disabled: false
        },
        {
          name: 'print',
          label: 'Print',
          value: 'print',
          disabled: false
        }
      ],
      value: {
        name: 'digital',
        label: 'Digital',
        value: 'digital',
        disabled: false
      },
      multiple: true,
      sequential: false,
      required: true,
      disabled: false,
      description: '<a href="/learn-more" target="_blank">Learn More</a>'
    }
  ]
};

// Helper function to generate fixed timestamp between 2020 and 2025 based on index
function generateFixedTimestamp(index: number) {
  // Create a deterministic pattern of dates
  const baseDate = new Date('2020-01-01').getTime();
  const monthInMillis = 30 * 24 * 60 * 60 * 1000;
  return Math.floor((baseDate + (index * monthInMillis)) / 1000);
}

// Helper function to generate fixed grade ranges
function generateFixedGrades(index: number) {
  // Create deterministic grade ranges based on index
  const patterns = [
    // K-12 ranges (25% of items)
    Array.from({ length: 13 }, (_, i) => ({
      slug: i === 0 ? 'k' : `grade-${i}`,
      name: i === 0 ? 'K' : `${i}`,
      title: i === 0 ? 'Kindergarten' : `Grade ${i}`,
      order: i
    })),
    // K-5 range
    Array.from({ length: 6 }, (_, i) => ({
      slug: i === 0 ? 'k' : `grade-${i}`,
      name: i === 0 ? 'K' : `${i}`,
      title: i === 0 ? 'Kindergarten' : `Grade ${i}`,
      order: i
    })),
    // 6-8 range
    Array.from({ length: 3 }, (_, i) => ({
      slug: `grade-${i + 6}`,
      name: `${i + 6}`,
      title: `Grade ${i + 6}`,
      order: i + 6
    })),
    // 9-12 range
    Array.from({ length: 4 }, (_, i) => ({
      slug: `grade-${i + 9}`,
      name: `${i + 9}`,
      title: `Grade ${i + 9}`,
      order: i + 9
    })),
    // K-2 range
    Array.from({ length: 3 }, (_, i) => ({
      slug: i === 0 ? 'k' : `grade-${i}`,
      name: i === 0 ? 'K' : `${i}`,
      title: i === 0 ? 'Kindergarten' : `Grade ${i}`,
      order: i
    })),
    // 3-5 range
    Array.from({ length: 3 }, (_, i) => ({
      slug: `grade-${i + 3}`,
      name: `${i + 3}`,
      title: `Grade ${i + 3}`,
      order: i + 3
    }))
  ];
  
  // Use modulo to cycle through patterns, ensuring K-12 appears ~25% of the time
  const patternIndex = index % 4 === 0 ? 0 : (index % patterns.length);
  return patterns[patternIndex];
}

// Helper function to generate variations of items
function generateItemVariations(baseItems: any[], count: number) {
  const variations = [...baseItems];
  const subjects = ['ela', 'math', 'science'];
  const formats = ['core-comprehensive', 'supplemental'];
  
  while (variations.length < count) {
    const index = variations.length;
    const baseItem = baseItems[index % baseItems.length];
    const newItem = {
      ...baseItem,
      slug: `${baseItem.slug}-${index + 1}`,
      title: `${baseItem.title} - Set ${index + 1}`,
      subject: {
        ...baseItem.subject,
        slug: subjects[index % subjects.length],
        name: subjects[index % subjects.length].toUpperCase(),
        title: subjects[index % subjects.length].toUpperCase()
      },
      format: {
        ...baseItem.format,
        slug: formats[index % formats.length],
        name: formats[index % formats.length] === 'core-comprehensive' ? 'Core: Comprehensive' : 'Supplemental',
        type: formats[index % formats.length] === 'core-comprehensive' ? 'Core' : 'Supplemental',
        subtype: formats[index % formats.length] === 'core-comprehensive' ? 'Comprehensive' : 'General',
        icon: {
          uri: formats[index % formats.length] === 'core-comprehensive' ? getAssetUrl(iconFormat2) : getAssetUrl(iconFormat1),
          name: formats[index % formats.length] === 'core-comprehensive' ? 'Core Comprehensive' : 'Supplemental',
          extension: '.svg',
          mime_type: 'image/svg+xml',
          alt: formats[index % formats.length] === 'core-comprehensive' ? 'Core Comprehensive Icon' : 'Supplemental Icon',
          width: 26,
          height: 26
        }
      },
      grades: generateFixedGrades(index),
      release: generateFixedTimestamp(index)
    };
    variations.push(newItem);
  }
  return variations;
}

export const mockSeriesOverview = {
  status: 'success',
  total: 50,
  items: generateItemVariations([
    {
      slug: 'serial-city-science-2022',
      title: 'Serial City Science 2022',
      cover: {
        uri: getAssetUrl(coverImage),
        name: 'Serial City Science 2022 Cover',
        extension: '.jpg',
        mime_type: 'image/jpeg',
        alt: 'Serial City Science 2022 Cover Image',
        width: 300,
        height: 400
      },
      subject: {
        slug: 'science',
        name: 'Science',
        title: 'Science'
      },
      format: {
        slug: 'core-comprehensive',
        name: 'Core: Comprehensive',
        type: 'Core',
        subtype: 'Comprehensive',
        icon: {
          uri: getAssetUrl(iconFormat2),
          name: 'Core Comprehensive',
          extension: '.svg',
          mime_type: 'image/svg+xml',
          alt: 'Core Comprehensive Icon',
          width: 26,
          height: 26
        }
      },
      grades: [
        {
          slug: 'k',
          name: 'K',
          title: 'Kindergarten',
          order: 0
        },
        {
          slug: 'first-grade',
          name: '1',
          title: 'First Grade',
          order: 1
        }
      ],
      release: generateFixedTimestamp(0),
      version: 'v2.0',
      gateways: [
        {
          slug: 'gateway-1',
          label: 'Gateway 1',
          name: 'Text Quality & Complexity',
          title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
          score: '91%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 0,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-2',
          label: 'Gateway 2',
          name: 'Building Knowledge with Texts, Topics, and Tasks',
          title: 'Building Knowledge with Texts, Topics, and Tasks',
          score: '88%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 1,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-3',
          label: 'Gateway 3',
          name: 'Usability',
          title: 'Usability',
          score: '75%',
          status: {
            slug: 'does-not-meet',
            name: 'Does Not Meet',
            title: 'Does Not Meet Expectations'
          },
          order: 2,
          failed_non_negotiable: true
        }
      ],
      details: [
        {
          label: '<a href="#glossary%3Ascience-of-reading">Science of Reading</a>',
          value: 'Available'
        }
      ],
      groups: ['science-core-k-2'],
      alerts: [
        {
          class: 'alert-primary',
          content: 'Under Publisher Review <a href="/read-more" target="_blank">Read More</a>'
        }
      ],
      copyright: '&#169; 2024 &#8211; Serial City Publishing'
    },
    {
      slug: 'math-mastery-2023',
      title: 'Math Mastery 2023',
      cover: {
        uri: getAssetUrl(coverImage),
        name: 'Math Mastery 2023 Cover',
        extension: '.jpg',
        mime_type: 'image/jpeg',
        alt: 'Math Mastery 2023 Cover Image',
        width: 300,
        height: 400
      },
      subject: {
        slug: 'math',
        name: 'Math',
        title: 'Mathematics'
      },
      format: {
        slug: 'core-comprehensive',
        name: 'Core: Comprehensive',
        type: 'Core',
        subtype: 'Comprehensive',
        icon: {
          uri: getAssetUrl(iconFormat2),
          name: 'Core Comprehensive',
          extension: '.svg',
          mime_type: 'image/svg+xml',
          alt: 'Core Comprehensive Icon',
          width: 26,
          height: 26
        }
      },
      grades: [
        {
          slug: 'k',
          name: 'K',
          title: 'Kindergarten',
          order: 0
        },
        {
          slug: 'first-grade',
          name: '1',
          title: 'First Grade',
          order: 1
        }
      ],
      release: generateFixedTimestamp(1),
      version: 'v2.0',
      gateways: [
        {
          slug: 'gateway-1',
          label: 'Gateway 1',
          name: 'Text Quality & Complexity',
          title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
          score: '88%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 0,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-2',
          label: 'Gateway 2',
          name: 'Building Knowledge with Texts, Topics, and Tasks',
          title: 'Building Knowledge with Texts, Topics, and Tasks',
          score: '88%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 1,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-3',
          label: 'Gateway 3',
          name: 'Usability',
          title: 'Usability',
          score: '75%',
          status: {
            slug: 'does-not-meet',
            name: 'Does Not Meet',
            title: 'Does Not Meet Expectations'
          },
          order: 2,
          failed_non_negotiable: true
        }
      ],
      details: [
        {
          label: '<a href="#glossary%3Ascience-of-reading">Science of Reading</a>',
          value: 'Available'
        }
      ],
      groups: ['math-core-k-2'],
      alerts: [],
      copyright: '&#169; 2024 &#8211; Math Mastery Publishing'
    },
    {
      slug: 'ela-essentials-2023',
      title: 'ELA Essentials 2023',
      cover: {
        uri: getAssetUrl(coverImage),
        name: 'ELA Essentials 2023 Cover',
        extension: '.jpg',
        mime_type: 'image/jpeg',
        alt: 'ELA Essentials 2023 Cover Image',
        width: 300,
        height: 400
      },
      subject: {
        slug: 'ela',
        name: 'ELA',
        title: 'English Language Arts'
      },
      format: {
        slug: 'core-comprehensive',
        name: 'Core: Comprehensive',
        type: 'Core',
        subtype: 'Comprehensive',
        icon: {
          uri: getAssetUrl(iconFormat2),
          name: 'Core Comprehensive',
          extension: '.svg',
          mime_type: 'image/svg+xml',
          alt: 'Core Comprehensive Icon',
          width: 26,
          height: 26
        }
      },
      grades: [
        {
          slug: 'k',
          name: 'K',
          title: 'Kindergarten',
          order: 0
        }
      ],
      release: generateFixedTimestamp(2),
      version: 'v2.0',
      gateways: [
        {
          slug: 'gateway-1',
          label: 'Gateway 1',
          name: 'Text Quality & Complexity',
          title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
          score: '95%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 0,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-2',
          label: 'Gateway 2',
          name: 'Building Knowledge with Texts, Topics, and Tasks',
          title: 'Building Knowledge with Texts, Topics, and Tasks',
          score: '88%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 1,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-3',
          label: 'Gateway 3',
          name: 'Usability',
          title: 'Usability',
          score: '75%',
          status: {
            slug: 'does-not-meet',
            name: 'Does Not Meet',
            title: 'Does Not Meet Expectations'
          },
          order: 2,
          failed_non_negotiable: true
        }
      ],
      details: [
        {
          label: '<a href="#glossary%3Ascience-of-reading">Science of Reading</a>',
          value: 'Available'
        }
      ],
      groups: ['ela-core-k'],
      alerts: [],
      copyright: '&#169; 2017 &#8211; ELA Essentials Publishing'
    },
    {
      slug: 'science-supplement-2023',
      title: 'Science Supplement 2023',
      cover: {
        uri: getAssetUrl(coverImage),
        name: 'Science Supplement 2023 Cover',
        extension: '.jpg',
        mime_type: 'image/jpeg',
        alt: 'Science Supplement 2023 Cover Image',
        width: 300,
        height: 400
      },
      subject: {
        slug: 'science',
        name: 'Science',
        title: 'Science'
      },
      format: {
        slug: 'supplemental',
        name: 'Supplemental',
        type: 'Supplemental',
        subtype: 'General',
        icon: {
          uri: getAssetUrl(iconFormat1),
          name: 'Supplemental',
          extension: '.svg',
          mime_type: 'image/svg+xml',
          alt: 'Supplemental Icon',
          width: 26,
          height: 26
        }
      },
      grades: [
        {
          slug: 'k',
          name: 'K',
          title: 'Kindergarten',
          order: 0
        }
      ],
      release: generateFixedTimestamp(3),
      version: 'v2.0',
      gateways: [
        {
          slug: 'gateway-1',
          label: 'Gateway 1',
          name: 'Text Quality & Complexity',
          title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
          score: '82%',
          status: {
            slug: 'partially-meets',
            name: 'Partially Meets',
            title: 'Partially Meets Expectations'
          },
          order: 0,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-2',
          label: 'Gateway 2',
          name: 'Building Knowledge with Texts, Topics, and Tasks',
          title: 'Building Knowledge with Texts, Topics, and Tasks',
          score: '88%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 1,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-3',
          label: 'Gateway 3',
          name: 'Usability',
          title: 'Usability',
          score: '75%',
          status: {
            slug: 'does-not-meet',
            name: 'Does Not Meet',
            title: 'Does Not Meet Expectations'
          },
          order: 2,
          failed_non_negotiable: true
        }
      ],
      details: [
        {
          label: '<a href="#glossary%3Ascience-of-reading">Science of Reading</a>',
          value: 'Available'
        }
      ],
      groups: ['science-supplement-k'],
      alerts: [],
      copyright: '&#169; 2021 &#8211; Science Supplement Publishing'
    },
    {
      slug: 'math-practice-2023',
      title: 'Math Practice 2023',
      cover: {
        uri: getAssetUrl(coverImage),
        name: 'Math Practice 2023 Cover',
        extension: '.jpg',
        mime_type: 'image/jpeg',
        alt: 'Math Practice 2023 Cover Image',
        width: 300,
        height: 400
      },
      subject: {
        slug: 'math',
        name: 'Math',
        title: 'Mathematics'
      },
      format: {
        slug: 'supplemental',
        name: 'Supplemental',
        type: 'Supplemental',
        subtype: 'General',
        icon: {
          uri: getAssetUrl(iconFormat1),
          name: 'Supplemental',
          extension: '.svg',
          mime_type: 'image/svg+xml',
          alt: 'Supplemental Icon',
          width: 26,
          height: 26
        }
      },
      grades: [
        {
          slug: 'first-grade',
          name: '1',
          title: 'First Grade',
          order: 1
        }
      ],
      release: generateFixedTimestamp(4),
      version: 'v2.0',
      gateways: [
        {
          slug: 'gateway-1',
          label: 'Gateway 1',
          name: 'Text Quality & Complexity',
          title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
          score: '85%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 0,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-2',
          label: 'Gateway 2',
          name: 'Building Knowledge with Texts, Topics, and Tasks',
          title: 'Building Knowledge with Texts, Topics, and Tasks',
          score: '88%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 1,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-3',
          label: 'Gateway 3',
          name: 'Usability',
          title: 'Usability',
          score: '75%',
          status: {
            slug: 'does-not-meet',
            name: 'Does Not Meet',
            title: 'Does Not Meet Expectations'
          },
          order: 2,
          failed_non_negotiable: true
        }
      ],
      details: [
        {
          label: '<a href="#glossary%3Ascience-of-reading">Science of Reading</a>',
          value: 'Available'
        }
      ],
      groups: ['math-supplement-1'],
      alerts: [],
      copyright: '&#169; 2019 &#8211; Math Practice Publishing'
    },
    {
      slug: 'ela-reading-2023',
      title: 'ELA Reading 2023',
      cover: {
        uri: getAssetUrl(coverImage),
        name: 'ELA Reading 2023 Cover',
        extension: '.jpg',
        mime_type: 'image/jpeg',
        alt: 'ELA Reading 2023 Cover Image',
        width: 300,
        height: 400
      },
      subject: {
        slug: 'ela',
        name: 'ELA',
        title: 'English Language Arts'
      },
      format: {
        slug: 'core-comprehensive',
        name: 'Core: Comprehensive',
        type: 'Core',
        subtype: 'Comprehensive',
        icon: {
          uri: getAssetUrl(iconFormat2),
          name: 'Core Comprehensive',
          extension: '.svg',
          mime_type: 'image/svg+xml',
          alt: 'Core Comprehensive Icon',
          width: 26,
          height: 26
        }
      },
      grades: [
        {
          slug: 'second-grade',
          name: '2',
          title: 'Second Grade',
          order: 2
        }
      ],
      release: generateFixedTimestamp(5),
      version: 'v2.0',
      gateways: [
        {
          slug: 'gateway-1',
          label: 'Gateway 1',
          name: 'Text Quality & Complexity',
          title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
          score: '92%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 0,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-2',
          label: 'Gateway 2',
          name: 'Building Knowledge with Texts, Topics, and Tasks',
          title: 'Building Knowledge with Texts, Topics, and Tasks',
          score: '88%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 1,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-3',
          label: 'Gateway 3',
          name: 'Usability',
          title: 'Usability',
          score: '75%',
          status: {
            slug: 'does-not-meet',
            name: 'Does Not Meet',
            title: 'Does Not Meet Expectations'
          },
          order: 2,
          failed_non_negotiable: true
        }
      ],
      details: [
        {
          label: '<a href="#glossary%3Ascience-of-reading">Science of Reading</a>',
          value: 'Available'
        }
      ],
      groups: ['ela-core-2'],
      alerts: [],
      copyright: '&#169; 2023 &#8211; ELA Reading Publishing'
    },
    {
      slug: 'science-discovery-2023',
      title: 'Science Discovery 2023',
      cover: {
        uri: getAssetUrl(coverImage),
        name: 'Science Discovery 2023 Cover',
        extension: '.jpg',
        mime_type: 'image/jpeg',
        alt: 'Science Discovery 2023 Cover Image',
        width: 300,
        height: 400
      },
      subject: {
        slug: 'science',
        name: 'Science',
        title: 'Science'
      },
      format: {
        slug: 'core-comprehensive',
        name: 'Core: Comprehensive',
        type: 'Core',
        subtype: 'Comprehensive',
        icon: {
          uri: getAssetUrl(iconFormat2),
          name: 'Core Comprehensive',
          extension: '.svg',
          mime_type: 'image/svg+xml',
          alt: 'Core Comprehensive Icon',
          width: 26,
          height: 26
        }
      },
      grades: [
        {
          slug: 'second-grade',
          name: '2',
          title: 'Second Grade',
          order: 2
        }
      ],
      release: generateFixedTimestamp(6),
      version: 'v2.0',
      gateways: [
        {
          slug: 'gateway-1',
          label: 'Gateway 1',
          name: 'Text Quality & Complexity',
          title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
          score: '89%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 0,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-2',
          label: 'Gateway 2',
          name: 'Building Knowledge with Texts, Topics, and Tasks',
          title: 'Building Knowledge with Texts, Topics, and Tasks',
          score: '88%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 1,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-3',
          label: 'Gateway 3',
          name: 'Usability',
          title: 'Usability',
          score: '75%',
          status: {
            slug: 'does-not-meet',
            name: 'Does Not Meet',
            title: 'Does Not Meet Expectations'
          },
          order: 2,
          failed_non_negotiable: true
        }
      ],
      details: [
        {
          label: '<a href="#glossary%3Ascience-of-reading">Science of Reading</a>',
          value: 'Available'
        }
      ],
      groups: ['science-core-2'],
      alerts: [],
      copyright: '&#169; 2015 &#8211; Science Discovery Publishing'
    },
    {
      slug: 'math-basics-2023',
      title: 'Math Basics 2023',
      cover: {
        uri: getAssetUrl(coverImage),
        name: 'Math Basics 2023 Cover',
        extension: '.jpg',
        mime_type: 'image/jpeg',
        alt: 'Math Basics 2023 Cover Image',
        width: 300,
        height: 400
      },
      subject: {
        slug: 'math',
        name: 'Math',
        title: 'Mathematics'
      },
      format: {
        slug: 'supplemental',
        name: 'Supplemental',
        type: 'Supplemental',
        subtype: 'General',
        icon: {
          uri: getAssetUrl(iconFormat1),
          name: 'Supplemental',
          extension: '.svg',
          mime_type: 'image/svg+xml',
          alt: 'Supplemental Icon',
          width: 26,
          height: 26
        }
      },
      grades: [
        {
          slug: 'k',
          name: 'K',
          title: 'Kindergarten',
          order: 0
        }
      ],
      release: generateFixedTimestamp(7),
      version: 'v2.0',
      gateways: [
        {
          slug: 'gateway-1',
          label: 'Gateway 1',
          name: 'Text Quality & Complexity',
          title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
          score: '78%',
          status: {
            slug: 'partially-meets',
            name: 'Partially Meets',
            title: 'Partially Meets Expectations'
          },
          order: 0,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-2',
          label: 'Gateway 2',
          name: 'Building Knowledge with Texts, Topics, and Tasks',
          title: 'Building Knowledge with Texts, Topics, and Tasks',
          score: '88%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 1,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-3',
          label: 'Gateway 3',
          name: 'Usability',
          title: 'Usability',
          score: '75%',
          status: {
            slug: 'does-not-meet',
            name: 'Does Not Meet',
            title: 'Does Not Meet Expectations'
          },
          order: 2,
          failed_non_negotiable: true
        }
      ],
      details: [
        {
          label: '<a href="#glossary%3Ascience-of-reading">Science of Reading</a>',
          value: 'Available'
        }
      ],
      groups: ['math-supplement-k'],
      alerts: [],
      copyright: '&#169; 2024 &#8211; Math Basics Publishing'
    },
    {
      slug: 'ela-writing-2023',
      title: 'ELA Writing 2023',
      cover: {
        uri: getAssetUrl(coverImage),
        name: 'ELA Writing 2023 Cover',
        extension: '.jpg',
        mime_type: 'image/jpeg',
        alt: 'ELA Writing 2023 Cover Image',
        width: 300,
        height: 400
      },
      subject: {
        slug: 'ela',
        name: 'ELA',
        title: 'English Language Arts'
      },
      format: {
        slug: 'supplemental',
        name: 'Supplemental',
        type: 'Supplemental',
        subtype: 'General',
        icon: {
          uri: getAssetUrl(iconFormat1),
          name: 'Supplemental',
          extension: '.svg',
          mime_type: 'image/svg+xml',
          alt: 'Supplemental Icon',
          width: 26,
          height: 26
        }
      },
      grades: [
        {
          slug: 'first-grade',
          name: '1',
          title: 'First Grade',
          order: 1
        }
      ],
      release: generateFixedTimestamp(8),
      version: 'v2.0',
      gateways: [
        {
          slug: 'gateway-1',
          label: 'Gateway 1',
          name: 'Text Quality & Complexity',
          title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
          score: '87%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 0,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-2',
          label: 'Gateway 2',
          name: 'Building Knowledge with Texts, Topics, and Tasks',
          title: 'Building Knowledge with Texts, Topics, and Tasks',
          score: '88%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 1,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-3',
          label: 'Gateway 3',
          name: 'Usability',
          title: 'Usability',
          score: '75%',
          status: {
            slug: 'does-not-meet',
            name: 'Does Not Meet',
            title: 'Does Not Meet Expectations'
          },
          order: 2,
          failed_non_negotiable: true
        }
      ],
      details: [
        {
          label: '<a href="#glossary%3Ascience-of-reading">Science of Reading</a>',
          value: 'Available'
        }
      ],
      groups: ['ela-supplement-1'],
      alerts: [],
      copyright: '&#169; 2018 &#8211; ELA Writing Publishing'
    },
    {
      slug: 'ela-essentials-2023-2',
      title: 'ELA Essentials 2023 - Set 2',
      cover: {
        uri: getAssetUrl(coverImage),
        name: 'ELA Essentials 2023 Cover',
        extension: '.jpg',
        mime_type: 'image/jpeg',
        alt: 'ELA Essentials 2023 Cover Image',
        width: 300,
        height: 400
      },
      subject: {
        slug: 'ela',
        name: 'ELA',
        title: 'English Language Arts'
      },
      format: {
        slug: 'core-comprehensive',
        name: 'Core: Comprehensive',
        type: 'Core',
        subtype: 'Comprehensive',
        icon: {
          uri: getAssetUrl(iconFormat2),
          name: 'Core Comprehensive',
          extension: '.svg',
          mime_type: 'image/svg+xml',
          alt: 'Core Comprehensive Icon',
          width: 26,
          height: 26
        }
      },
      grades: [
        {
          slug: 'k',
          name: 'K',
          title: 'Kindergarten',
          order: 0
        }
      ],
      release: generateFixedTimestamp(9),
      version: 'v2.0',
      gateways: [
        {
          slug: 'gateway-1',
          label: 'Gateway 1',
          name: 'Text Quality & Complexity',
          title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
          score: '95%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 0,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-2',
          label: 'Gateway 2',
          name: 'Building Knowledge with Texts, Topics, and Tasks',
          title: 'Building Knowledge with Texts, Topics, and Tasks',
          score: '88%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          },
          order: 1,
          failed_non_negotiable: false
        },
        {
          slug: 'gateway-3',
          label: 'Gateway 3',
          name: 'Usability',
          title: 'Usability',
          score: '75%',
          status: {
            slug: 'does-not-meet',
            name: 'Does Not Meet',
            title: 'Does Not Meet Expectations'
          },
          order: 2,
          failed_non_negotiable: true
        }
      ],
      details: [
        {
          label: '<a href="#glossary%3Ascience-of-reading">Science of Reading</a>',
          value: 'Available'
        }
      ],
      groups: ['ela-core-k'],
      alerts: [],
      copyright: '&#169; 2022 &#8211; ELA Essentials Publishing'
    }
  ], 50)
};

export const mockSeriesDetail = {
  status: 'success',
  data: {
    ...mockSeriesOverview.items[0],
    overviews: [
      {
        summary: '<p>Comprehensive review of Serial City Science 2022 for grades K-2</p>',
        report_cards: [
          {
            grade: {
              slug: 'k',
              name: 'K',
              title: 'Kindergarten',
              order: 0
            },
            gateways: [
              {
                slug: 'gateway-1',
                label: 'Gateway 1',
                name: 'Text Quality & Complexity',
                title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
                score: '91%',
                status: {
                  slug: 'meets',
                  name: 'Meets',
                  title: 'Meets Expectations'
                },
                order: 0,
                failed_non_negotiable: false
              }
            ]
          }
        ],
        criteria_groups: [
          {
            title: '<a href="#glossary%3Ascience-of-reading">Science of Reading</a> Snapshot',
            summary: 'Overview of Science of Reading criteria',
            criteria: [
              {
                slug: 'criterion-1-1',
                label: 'Text Quality & Complexity',
                non_negotiable: true,
                scores: [
                  {
                    grade: {
                      slug: 'k',
                      name: 'K',
                      title: 'Kindergarten',
                      order: 0
                    },
                    score: '75%',
                    failed_non_negotiable: false
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  }
};

export const mockReportOverview = {
  status: 'success',
  data: {
    grade: {
      slug: 'k',
      name: 'K',
      title: 'Kindergarten',
      order: 0
    },
    gateways: [
      {
        slug: 'gateway-1',
        label: 'Gateway 1',
        name: 'Text Quality & Complexity',
        title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
        score: '91%',
        status: {
          slug: 'meets',
          name: 'Meets',
          title: 'Meets Expectations'
        },
        order: 0,
        failed_non_negotiable: false
      },
      {
        slug: 'gateway-2',
        label: 'Gateway 2',
        name: 'Building Knowledge with Texts, Topics, and Tasks',
        title: 'Building Knowledge with Texts, Topics, and Tasks',
        score: '88%',
        status: {
          slug: 'meets',
          name: 'Meets',
          title: 'Meets Expectations'
        },
        order: 1,
        failed_non_negotiable: false
      },
      {
        slug: 'gateway-3',
        label: 'Gateway 3',
        name: 'Usability',
        title: 'Usability',
        score: '75%',
        status: {
          slug: 'does-not-meet',
          name: 'Does Not Meet',
          title: 'Does Not Meet Expectations'
        },
        order: 2,
        failed_non_negotiable: true
      }
    ],
    summary: '<p>Detailed review of Serial City Science 2022 for Kindergarten</p>',
    details: [
      {
        title: 'Serial City Science 2022 Kindergarten',
        isbn: '978-0-123456-47-2',
        edition: 'First Edition',
        publisher: {
          slug: 'serial-city-publishing',
          name: 'Serial City Publishing'
        },
        copyright: '2024'
      }
    ],
    downloads: [
      {
        uri: '/downloads/reports/serial-city-science-2022-k.pdf',
        name: 'Full Report',
        extension: '.pdf',
        mime_type: 'application/pdf'
      }
    ],
    criteria: {
      uri: '/downloads/criteria/serial-city-science-2022-k.pdf',
      name: 'Criteria',
      extension: '.pdf',
      mime_type: 'application/pdf'
    },
    evidence_guide: {
      uri: '/downloads/evidence/serial-city-science-2022-k.pdf',
      name: 'Evidence Guide',
      extension: '.pdf',
      mime_type: 'application/pdf'
    }
  }
};

export const mockGatewayDetail = {
  status: 'success',
  data: {
    slug: 'gateway-1',
    label: 'Gateway 1',
    name: 'Text Quality & Complexity',
    title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
    score: '91%',
    status: {
      slug: 'meets',
      name: 'Meets',
      title: 'Meets Expectations'
    },
    order: 0,
    failed_non_negotiable: false,
    description: '<p>Overview of Gateway 1 criteria and requirements</p>',
    details: '<p>Detailed analysis of Gateway 1 performance</p>',
    criterion: [
      {
        label: 'Criterion 1.1',
        name: 'Text Quality & Complexity',
        title: 'Text Quality & Complexity and Alignment to the Standards with Tasks and Questions Grounded in Evidence',
        description: '<p>Description of Criterion 1.1</p>',
        details: '<p>Detailed analysis of Criterion 1.1</p>',
        rating: {
          score: 16,
          total: 20,
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations'
          }
        },
        order: 0,
        failed_non_negotiable: false,
        supports: [
          {
            type: {
              slug: 'multilingual-learner',
              label: 'Multilingual Learner Supports'
            },
            criterion: {
              label: 'Support Criterion 1.1',
              name: 'Multilingual Support',
              title: 'Multilingual Learner Support for Text Quality',
              description: '<p>Description of multilingual support</p>',
              details: '<p>Detailed analysis of multilingual support</p>',
              rating: {
                score: 8,
                total: 10,
                status: {
                  slug: 'meets',
                  name: 'Meets',
                  title: 'Meets Expectations'
                }
              },
              order: 0,
              failed_non_negotiable: false
            }
          }
        ],
        indicators: [
          {
            label: 'Indicator 1A',
            description: '<p>Description of Indicator 1A</p>',
            details: '<p>Detailed analysis of Indicator 1A</p>',
            rating: {
              score: 8,
              total: 10,
              status: {
                slug: 'meets',
                name: 'Meets',
                title: 'Meets Expectations'
              }
            },
            order: 0,
            failed_non_negotiable: false,
            supports: [
              {
                type: {
                  slug: 'multilingual-learner',
                  label: 'Multilingual Learner Supports'
                },
                indicator: {
                  label: 'Support Indicator 1A',
                  description: '<p>Description of multilingual support indicator</p>',
                  details: '<p>Detailed analysis of multilingual support indicator</p>',
                  rating: {
                    score: 4,
                    total: 5,
                    status: {
                      slug: 'meets',
                      name: 'Meets',
                      title: 'Meets Expectations'
                    }
                  },
                  order: 0,
                  failed_non_negotiable: false
                }
              }
            ],
            claims: []
          }
        ]
      }
    ]
  }
}; 